import type { MenuItem, StoreMetadata, StoreSettings } from "@/types";
import Head from "next/head";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useAppSelector } from "@/store";

import { siteOrigin } from "@/lib/config";
import { formattedStoreName } from "@/utils/format";

const clean = (value?: string | null): string | undefined =>
  value?.trim() || undefined;

/**
 * Renders the full <head> for a page: title, Open Graph / Twitter cards,
 * favicons, theme colour and the Restaurant JSON-LD block.
 */
interface SeoHeadProps {
  pageName?: string;
  zoomEnabled?: boolean;
  ssrSettings?: StoreSettings | null;
  ssrMetadata?: StoreMetadata | null;
  baseUrl?: string;
  seoItem?: MenuItem | null;
}

export default function SeoHead({
  pageName,
  zoomEnabled,
  ssrSettings,
  ssrMetadata,
  baseUrl,
  seoItem,
}: SeoHeadProps) {
  const router = useRouter();
  const settings = useAppSelector((state) => state.settings.data);

  const fallback = useMemo(() => {
    if (!ssrMetadata) return ssrSettings;
    return {
      public_business_name:
        ssrMetadata.public_business_name ?? ssrMetadata.title,
      web_favicon: ssrMetadata.web_favicon ?? ssrMetadata.logo,
      brand_color: ssrMetadata.brand_color,
      store_description:
        ssrMetadata.store_description ?? ssrMetadata.description,
      restaurant_address: ssrMetadata.restaurant_address,
      city: ssrMetadata.city,
      business_type: ssrMetadata.business_type,
      image_url: ssrMetadata.image_url,
      fb_link: ssrMetadata.fb_link,
      instagram_link: ssrMetadata.instagram_link,
      twitter_link: ssrMetadata.twitter_link,
      tiktok_link: ssrMetadata.tiktok_link,
    };
  }, [ssrMetadata, ssrSettings]);

  const source = settings?.public_business_name ? settings : (fallback ?? {});
  const {
    public_business_name: businessName,
    web_favicon: favicon,
    brand_color: brandColor,
    store_description: description,
    restaurant_address: address,
    city,
    business_type: businessType,
    image_url: imageUrl,
    fb_link: fbLink,
    instagram_link: instagramLink,
    twitter_link: twitterLink,
    tiktok_link: tiktokLink,
  } = source;

  // The original builds this from `window.location` at runtime, with an
  // explicit override taking precedence. `baseUrl` arrives from
  // `getServerSideProps` already resolved against the request host, so the
  // client recomputes the same value and nothing mismatches on hydration.
  const origin = baseUrl || siteOrigin();
  const path = router.asPath ? router.asPath.split("?")[0] : "";
  const canonical = origin ? `${origin}${path}` : undefined;

  const sameAs = useMemo(
    () =>
      [fbLink, instagramLink, twitterLink, tiktokLink]
        .map(clean)
        .filter(Boolean),
    [fbLink, instagramLink, twitterLink, tiktokLink],
  );

  const { title, metadata, jsonLd } = useMemo(() => {
    const name = clean(businessName);
    const storeName = name ? formattedStoreName(name) : undefined;
    const suffix = clean(seoItem?.name) || clean(pageName);
    const pageTitle = storeName
      ? `${storeName}${suffix ? ` - ${suffix}` : ""}`
      : suffix;

    const icon = clean(favicon) || "/zenvix-icon.png";
    const color = clean(brandColor);
    const storeDescription = clean(seoItem?.description) || clean(description);
    const streetAddress = clean(address);
    const addressLocality = clean(city);
    const cuisine = clean(businessType);
    const image = clean(seoItem?.image_url) || clean(imageUrl) || icon;

    const postalAddress =
      streetAddress || addressLocality
        ? {
            "@type": "PostalAddress",
            ...(streetAddress ? { streetAddress } : {}),
            ...(addressLocality ? { addressLocality } : {}),
          }
        : undefined;

    return {
      title: pageTitle,
      metadata:
        storeName || icon || color || storeDescription || streetAddress || image
          ? {
              public_business_name: storeName,
              web_favicon: icon,
              brand_color: color,
              store_description: storeDescription,
              restaurant_address: streetAddress,
              image_url: image,
            }
          : null,
      jsonLd: seoItem
        ? {
            "@context": "https://schema.org",
            "@type": "Product",
            name: seoItem.name,
            ...(storeDescription ? { description: storeDescription } : {}),
            ...(image ? { image } : {}),
            ...(canonical ? { url: canonical } : {}),
            ...(seoItem.price != null
              ? { offers: { "@type": "Offer", price: seoItem.discounted_price ?? seoItem.price, priceCurrency: ssrSettings?.currency_code || "PKR", availability: seoItem.sold_out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" } }
              : {}),
          }
        :
        storeName ||
        storeDescription ||
        image ||
        canonical ||
        postalAddress ||
        cuisine
          ? {
              "@context": "https://schema.org",
              "@type": "Restaurant",
              ...(storeName ? { name: storeName } : {}),
              ...(image ? { image } : {}),
              ...(storeDescription ? { description: storeDescription } : {}),
              ...(canonical ? { url: canonical } : {}),
              ...(postalAddress ? { address: postalAddress } : {}),
              ...(cuisine ? { servesCuisine: cuisine } : {}),
              ...(sameAs.length ? { sameAs } : {}),
            }
          : null,
    };
  }, [
    pageName,
    businessName,
    favicon,
    brandColor,
    description,
    address,
    city,
    businessType,
    imageUrl,
    canonical,
    sameAs,
    seoItem,
    ssrSettings?.currency_code,
  ]);

  return (
    <Head>
      {!zoomEnabled && (
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
      )}
      {title && <title key="title">{title}</title>}
      {title && <meta key="og:title" property="og:title" content={title} />}
      {title && (
        <meta key="twitter:title" name="twitter:title" content={title} />
      )}

      {metadata?.public_business_name && (
        <meta
          key="og:site_name"
          property="og:site_name"
          content={metadata.public_business_name}
        />
      )}

      {canonical && (
        <>
          <meta key="og:url" property="og:url" content={canonical} />
          <meta key="twitter:url" name="twitter:url" content={canonical} />
          <link key="canonical" rel="canonical" href={canonical} />
        </>
      )}

      {metadata?.image_url && (
        <>
          <meta
            key="og:image"
            property="og:image"
            content={metadata.image_url}
          />
          <meta
            key="og:image:secure_url"
            property="og:image:secure_url"
            content={metadata.image_url}
          />
          <meta
            key="twitter:card"
            name="twitter:card"
            content="summary_large_image"
          />
          <meta
            key="twitter:image"
            name="twitter:image"
            content={metadata.image_url}
          />
        </>
      )}

      {metadata?.store_description && (
        <>
          <meta
            key="description"
            name="description"
            content={metadata.store_description}
          />
          <meta
            key="og:description"
            property="og:description"
            content={metadata.store_description}
          />
          <meta
            key="twitter:description"
            property="twitter:description"
            content={metadata.store_description}
          />
        </>
      )}

      {metadata?.brand_color && (
        <meta
          key="theme-color"
          name="theme-color"
          content={metadata.brand_color}
        />
      )}

      {metadata?.web_favicon && (
        <>
          <link key="icon" rel="icon" href={metadata.web_favicon} />
          <link
            key="apple-touch-icon"
            rel="apple-touch-icon"
            href={metadata.web_favicon}
            type="image/png"
          />
          <link
            key="shortcut-icon"
            rel="shortcut icon"
            href={metadata.web_favicon}
            type="image/png"
          />
        </>
      )}

      {metadata?.restaurant_address && (
        <meta
          key="address"
          name="address"
          content={metadata.restaurant_address}
        />
      )}

      {jsonLd && (
        <script
          key="json-ld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </Head>
  );
}
